import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useFloating, autoUpdate, shift, offset } from "@floating-ui/react";
import ChevronDownIcon from "mdi-react/ChevronDownIcon";
import ChevronUpIcon from "mdi-react/ChevronUpIcon";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import CloseIcon from "mdi-react/CloseIcon";
import CheckIcon from "mdi-react/CheckIcon";

const triggerStyle = (disabled) => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: 12,
  background: "white",
  border: "1px solid #D1D5DB",
  borderRadius: 8,
  boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  transition: "border-color 0.2s",
  outline: "none",
  font: "inherit",
});

const dropdownContainer = {
  background: "white",
  border: "1px solid #E5E7EB",
  borderRadius: 8,
  boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
  maxHeight: 384,
  overflow: "hidden",
};

export default function SearchableSelector({
  items = [],
  selectedItem,
  onItemSelect,
  loading = false,
  disabled = false,
  placeholder = "Seleccionar item",
  searchPlaceholder = "Buscar...",
  icon: IconComponent,
  showAllOption = false,
  allOptionText = "Todos los items",
  allOptionDescription = "Ver todos",
  itemDescription,
  showItemId = false,
  maxDisplayItems = 100,
  emptyStateText = "No se encontraron items",
  emptyStateSubtext = "Intenta con otro término de búsqueda",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    placement: "bottom-start",
    middleware: [offset(8), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const filteredItems = useMemo(() => {
    if (!searchTerm) return items;
    return items.filter(
      (item) => item && item.label && item.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, items]);

  const handleClickOutside = useCallback((event) => {
    const clickedTrigger = refs.reference.current && refs.reference.current.contains(event.target);
    const clickedFloating = refs.floating.current && refs.floating.current.contains(event.target);
    if (!clickedTrigger && !clickedFloating) setIsOpen(false);
  }, [refs]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, handleClickOutside]);

  const handleSelect = (item) => {
    if (onItemSelect) onItemSelect(item);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleSelectAll = () => {
    if (onItemSelect) onItemSelect(null);
    setIsOpen(false);
    setSearchTerm("");
  };

  const toggleOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!loading && !disabled) setIsOpen(!isOpen);
  };

  const selectedName = useMemo(() => {
    if (selectedItem == null) return showAllOption ? allOptionText : placeholder;
    const found = items.find((i) => i && i.value === selectedItem);
    return found ? found.label : showAllOption ? allOptionText : placeholder;
  }, [selectedItem, items, placeholder, showAllOption, allOptionText]);

  const selectedDescription = useMemo(() => {
    if (selectedItem == null) {
      return showAllOption ? allOptionDescription : `${items.length} items disponibles`;
    }
    if (itemDescription && typeof itemDescription === "function") {
      return itemDescription(selectedItem, items);
    }
    return "Item seleccionado";
  }, [selectedItem, items, showAllOption, allOptionDescription, itemDescription]);

  const displayedItems = useMemo(() => filteredItems.slice(0, maxDisplayItems), [filteredItems, maxDisplayItems]);

  const dropdownContent = (
    <>
      <div style={{ padding: 12, borderBottom: "1px solid #E5E7EB" }}>
        <div style={{ position: "relative" }}>
          <MagnifyIcon size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9CA3AF" }} />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
            style={{
              width: "100%",
              paddingLeft: 36,
              paddingRight: 36,
              paddingTop: 8,
              paddingBottom: 8,
              border: "1px solid #D1D5DB",
              borderRadius: 6,
              outline: "none",
              fontSize: 14,
              boxSizing: "border-box",
            }}
          />
          {searchTerm && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSearchTerm(""); }}
              style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", color: "#9CA3AF", cursor: "pointer", padding: 0, display: "flex" }}
            >
              <CloseIcon size={16} />
            </button>
          )}
        </div>
      </div>

      <div style={{ maxHeight: 256, overflowY: "auto" }}>
        {showAllOption && (
          <button
            type="button"
            onClick={handleSelectAll}
            style={itemRowStyle(selectedItem == null)}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {IconComponent && <IconComponent size={18} color={selectedItem == null ? "#2563EB" : "#6B7280"} />}
              <div>
                <div style={{ fontWeight: 500 }}>{allOptionText}</div>
                <div style={{ fontSize: 12, color: "#6B7280" }}>{allOptionDescription}</div>
              </div>
            </div>
            {selectedItem == null && <CheckIcon size={16} color="#2563EB" />}
          </button>
        )}

        {displayedItems.length === 0 && searchTerm ? (
          <div style={{ padding: "32px 16px", textAlign: "center", color: "#6B7280" }}>
            {IconComponent && <IconComponent size={24} style={{ display: "block", margin: "0 auto 8px", color: "#D1D5DB" }} />}
            <div style={{ fontSize: 14 }}>{emptyStateText}</div>
            <div style={{ fontSize: 12 }}>{emptyStateSubtext}</div>
          </div>
        ) : (
          displayedItems.map((item) => {
            const isSelected = selectedItem === item.value;
            return (
              <button
                key={item.value}
                type="button"
                onClick={() => handleSelect(item)}
                style={itemRowStyle(isSelected)}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
                  {IconComponent && <IconComponent size={18} color={isSelected ? "#2563EB" : "#6B7280"} />}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</div>
                    {(showItemId || item.description) && (
                      <div style={{ fontSize: 12, color: "#6B7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {showItemId && `ID: ${item.value}`}
                        {showItemId && item.description && " • "}
                        {item.description}
                      </div>
                    )}
                  </div>
                </div>
                {isSelected && <CheckIcon size={16} color="#2563EB" />}
              </button>
            );
          })
        )}

        {filteredItems.length > maxDisplayItems && (
          <div style={{ padding: "8px 16px", textAlign: "center", fontSize: 12, color: "#6B7280", background: "#F9FAFB" }}>
            Mostrando los primeros {maxDisplayItems} resultados de {filteredItems.length}
          </div>
        )}
      </div>

      <div style={{ padding: 12, borderTop: "1px solid #E5E7EB", background: "#F9FAFB" }}>
        <div style={{ fontSize: 12, color: "#6B7280", textAlign: "center" }}>
          {items.length} items totales
        </div>
      </div>
    </>
  );

  return (
    <div style={{ width: "100%" }}>
      <div style={{ position: "relative", width: "100%" }} ref={dropdownRef}>
        <button
          ref={refs.setReference}
          type="button"
          onClick={toggleOpen}
          disabled={loading || disabled}
          style={triggerStyle(loading || disabled)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            {IconComponent && <IconComponent size={20} color="#6B7280" />}
            <div style={{ textAlign: "left", minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {selectedName}
              </div>
              <div style={{ fontSize: 12, color: "#6B7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {selectedDescription}
              </div>
            </div>
          </div>
          {loading ? (
            <div style={{ width: 16, height: 16, border: "2px solid #6B7280", borderBottomColor: "transparent", borderRadius: "50%", animation: "ssSpin 1s linear infinite" }} />
          ) : (
            isOpen ? <ChevronUpIcon size={16} color="#6B7280" /> : <ChevronDownIcon size={16} color="#6B7280" />
          )}
        </button>
        <style>{`@keyframes ssSpin { 100% { transform: rotate(360deg); } }`}</style>
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              width: refs.reference.current?.offsetWidth || "auto",
              zIndex: 999999,
              ...dropdownContainer,
            }}
          >
            {dropdownContent}
          </div>,
          document.body
        )}
    </div>
  );
}

function itemRowStyle(isSelected) {
  return {
    width: "100%",
    padding: "12px 16px",
    textAlign: "left",
    background: isSelected ? "#EFF6FF" : "white",
    color: isSelected ? "#1D4ED8" : "#374151",
    border: "none",
    borderBottom: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    transition: "background 0.15s",
    font: "inherit",
  };
}
